// Lógica da tela "Comparar planos".
// Cada seguradora dá um nome diferente para a mesma cobertura (ex: "Despesas
// Farmacêuticas" x "Despesas com Medicamentos"). Aqui juntamos os nomes
// equivalentes em linhas comuns, para comparar lado a lado.
import {
  coversCovid,
  findAmounts,
  formatAmount,
  getLuggageText,
  getTotalPrice,
  medicalCoverageValue,
  meetsSchengen,
  parseMedicalCoverage,
  pickAmount,
} from './coverage.utils';

export const MAX_COMPARE = 3;

// Remove acentos e deixa minúsculo, para comparar nomes de cobertura.
const normalize = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Linhas da comparação, agrupadas por seção.
// `keywords`: trechos procurados no nome da cobertura (qualquer um serve).
// `exclude`: trechos que descartam o item (ex: "coordenação de traslado").
export const COMPARE_SECTIONS = [
  {
    title: 'Saúde',
    rows: [
      { key: 'medical', label: 'Despesas médicas e hospitalares', special: 'medical' },
      { key: 'covid', label: 'Covid-19', special: 'covid' },
      { key: 'preexisting', label: 'Doença preexistente', keywords: ['preexistente'] },
      { key: 'dental', label: 'Despesas odontológicas', keywords: ['odontolog'] },
      { key: 'pharmacy', label: 'Medicamentos', keywords: ['farmac', 'medicamento'] },
      {
        key: 'medicalTransfer',
        label: 'Traslado médico',
        keywords: ['traslado medico'],
        exclude: ['coord'],
      },
      { key: 'sanitaryReturn', label: 'Regresso sanitário', keywords: ['regresso sanitario'] },
      { key: 'bodyTransfer', label: 'Traslado de corpo', keywords: ['traslado de corpo'] },
      { key: 'pregnancy', label: 'Gestantes', keywords: ['gestante'] },
      { key: 'sports', label: 'Prática de esportes', keywords: ['esporte'] },
      { key: 'telemedicine', label: 'Telemedicina', keywords: ['telemedicina'] },
    ],
  },
  {
    title: 'Bagagem e voo',
    rows: [
      { key: 'luggage', label: 'Extravio de bagagem', special: 'luggage' },
      { key: 'luggageDelay', label: 'Atraso de bagagem', keywords: ['atraso de bagagem'] },
      { key: 'luggageDamage', label: 'Danos à mala', keywords: ['danos a mala', 'danos as malas'] },
      {
        key: 'flightDelay',
        label: 'Atraso ou cancelamento de voo',
        keywords: ['atraso de voo', 'atraso de embarque', 'cancelamento de voo'],
      },
      { key: 'vipLounge', label: 'Sala VIP em atraso de voo', keywords: ['vip delay', 'smart delay'] },
    ],
  },
  {
    title: 'Imprevistos na viagem',
    rows: [
      {
        key: 'tripCancel',
        label: 'Cancelamento de viagem',
        keywords: ['cancelamento de viagem', 'cancelamento/interrupcao'],
      },
      {
        key: 'tripInterrupt',
        label: 'Interrupção de viagem',
        keywords: ['interrupcao de viagem', 'cancelamento/interrupcao'],
      },
      {
        key: 'companion',
        label: 'Acompanhante em caso de internação',
        keywords: [
          'hospedagem de acompanhante',
          'acompanhante em caso de hospitalizacao',
          'segurado hospitalizado',
        ],
      },
      {
        key: 'extendedStay',
        label: 'Prorrogação de estadia',
        keywords: ['prorrogacao de estadia', 'hospedagem em hotel apos alta'],
        exclude: ['coord'],
      },
      { key: 'legal', label: 'Despesas jurídicas', keywords: ['juridic'] },
      { key: 'bail', label: 'Fiança', keywords: ['fianca'] },
    ],
  },
  {
    title: 'Outros',
    rows: [
      { key: 'death', label: 'Morte acidental', keywords: ['morte acidental'] },
      { key: 'disability', label: 'Invalidez por acidente', keywords: ['invalidez'] },
      // "pet" com espaço/aspas antes, para não casar com palavras como "competição".
      { key: 'pet', label: 'Despesas com pet', keywords: [' pet', '“pet', '"pet'] },
      { key: 'age', label: 'Limite de idade', special: 'age' },
    ],
  },
];

const INCLUDED_REGEX = /^(sim|incluido|incluso)/i;

// Encontra o item de cobertura da linha. Se houver mais de um (a Assist Card
// manda "Traslado médico: INCLUIDO" e "Traslado Medico EUR 15.000"), prefere o que tem valor.
const findCoverageItem = (coverageList, row) => {
  const matches = (coverageList || []).filter((item) => {
    const name = normalize(item.description);
    return (
      row.keywords.some((kw) => name.includes(kw)) &&
      !(row.exclude || []).some((kw) => name.includes(kw))
    );
  });
  return matches.find((item) => findAmounts(item.value, 1).length) ?? matches[0] ?? null;
};

// Valor de uma célula da tabela.
//   status: 'amount' (tem valor), 'included' (incluso, sem valor), 'no' (não cobre),
//           'unknown' (a seguradora não informou), 'text' (texto livre)
//   score:  número usado para descobrir o maior valor da linha
export const getCell = (insurance, row, isEurope) => {
  if (row.special === 'medical') {
    const amount = parseMedicalCoverage(insurance.healthCoverage, isEurope);
    return amount
      ? {
          status: 'amount',
          display: formatAmount(amount),
          score: amount.amount,
          currency: amount.currency,
        }
      : { status: 'text', display: insurance.healthCoverage || '—' };
  }
  if (row.special === 'luggage') {
    // Valor exibido e valor comparado são o mesmo (euro na Europa, dólar fora).
    const amount = pickAmount(findAmounts(getLuggageText(insurance), 1), isEurope);
    return amount
      ? {
          status: 'amount',
          display: formatAmount(amount),
          score: amount.amount,
          currency: amount.currency,
        }
      : { status: 'no', display: 'Não cobre' };
  }
  if (row.special === 'covid') {
    return coversCovid(insurance)
      ? { status: 'included', display: 'Coberto' }
      : { status: 'no', display: 'Não cobre' };
  }
  if (row.special === 'age') {
    const age = String(insurance.ageInfo || '').match(/(\d{2,3})\s*anos/i);
    return age
      ? { status: 'text', display: `Até ${age[1]} anos` }
      : { status: 'unknown', display: '—' };
  }

  const item = findCoverageItem(insurance.coverageList, row);
  if (!item) return { status: 'unknown', display: '—' };

  const value = String(item.value || '').trim();
  const amount = pickAmount(findAmounts(value, 1), isEurope);
  if (amount) {
    return {
      status: 'amount',
      display: formatAmount(amount),
      score: amount.amount,
      currency: amount.currency,
    };
  }
  if (/incluso no dmho/i.test(value)) {
    return { status: 'included', display: 'Incluso', note: 'dentro da despesa médica' };
  }
  if (INCLUDED_REGEX.test(normalize(value))) {
    return { status: 'included', display: 'Incluso' };
  }
  if (/^(nao|sem cobertura)/i.test(normalize(value))) {
    return { status: 'no', display: 'Não cobre' };
  }
  return { status: 'text', display: value };
};

// Monta a tabela inteira: para cada linha, as células de cada plano e quem
// tem o maior valor (só quando os valores são comparáveis e diferentes).
export const buildComparison = (plans, isEurope) =>
  COMPARE_SECTIONS.map((section) => ({
    ...section,
    rows: section.rows
      .filter((row) => row.key !== 'age' || plans.some((p) => p.ageInfo))
      .map((row) => {
        const cells = plans.map((plan) => getCell(plan, row, isEurope));
        const scored = cells.filter((c) => c.status === 'amount');
        const sameCurrency = new Set(scored.map((c) => (c.currency === 'BRL' ? 'BRL' : 'FX'))).size <= 1;
        const best = scored.length ? Math.max(...scored.map((c) => c.score)) : null;
        const allEqual = scored.length === cells.length && scored.every((c) => c.score === best);
        const winners =
          best && sameCurrency && !allEqual
            ? cells.map((c) => c.status === 'amount' && c.score === best)
            : cells.map(() => false);
        const differs = new Set(cells.map((c) => c.display)).size > 1;
        return { ...row, cells, winners, differs };
      })
      // Esconde linhas que nenhum dos planos informa.
      .filter((row) => row.cells.some((c) => c.status !== 'unknown')),
  })).filter((section) => section.rows.length);

// Quantas coberturas da tabela cada plano tem: { covered, total }.
// Conta como "tem" o que vem com valor ou como incluso. O que a seguradora não
// informou ("—") não conta. "Limite de idade" fica de fora (não é cobertura).
export const coverageCounts = (comparison, planCount) => {
  const counts = Array.from({ length: planCount }, () => ({ covered: 0, total: 0 }));
  comparison.forEach((section) =>
    section.rows.forEach((row) => {
      if (row.key === 'age') return;
      row.cells.forEach((cell, i) => {
        counts[i].total += 1;
        if (cell.status === 'included' || (cell.status === 'amount' && cell.score > 0)) {
          counts[i].covered += 1;
        }
      });
    })
  );
  return counts;
};

// Selos de cada plano:
//  - Melhor preço: menor preço total.
//  - Maior cobertura médica: maior valor de despesas médicas.
//  - Melhor custo-benefício: mais cobertura médica por real pago
//    (despesas médicas ÷ preço total).
export const planBadges = (plans, youngPassengers, oldPassengers) => {
  const prices = plans.map((p) => getTotalPrice(p, youngPassengers, oldPassengers));
  const medical = plans.map((p) => medicalCoverageValue(p.healthCoverage));
  const value = medical.map((m, i) => (prices[i] > 0 ? m / prices[i] : 0));
  const indexOfMax = (arr) => arr.indexOf(Math.max(...arr));
  const cheapest = prices.indexOf(Math.min(...prices));
  const mostMedical = indexOfMax(medical);
  const bestValue = indexOfMax(value);

  return plans.map((_, i) => {
    const badges = [];
    if (i === cheapest) badges.push('Melhor preço');
    if (i === mostMedical && new Set(medical).size > 1) badges.push('Maior cobertura médica');
    if (i === bestValue && Math.max(...value) > 0) badges.push('Melhor custo-benefício');
    return badges;
  });
};

export const planSchengen = (plan) => meetsSchengen(plan.healthCoverage);
